import React, { ReactNode } from 'react'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Theme } from '@emotion/react'
import { FusionColor } from '../../../theme/FusionStyle'
import { faSpinner } from '@fortawesome/pro-solid-svg-icons'
import { L1NotificationDot, L1NotificationDotPositioner } from './L1NotificationDot'

interface L1PlainButtonRootProps {
	color?: FusionColor
	onClick?: React.MouseEventHandler<HTMLButtonElement>
}

const L1PlainButtonRoot = styled.button<L1PlainButtonRootProps>(({theme, color, onClick}) => ({
	boxSizing: 'border-box',
	all: 'unset',
	'&:hover': {
		cursor: onClick ? 'pointer' : 'default',
	},
	color: color ? theme.colors[color] : 'inherit',
}))

interface L1PlainIconButtonProps {
	icon: IconProp
	color?: FusionColor
	onClick?: React.MouseEventHandler<HTMLButtonElement>
}

export const L1PlainIconButton = ({icon, color, onClick}: L1PlainIconButtonProps) => {
	return (
		<L1PlainButtonRoot color={color} onClick={onClick}>
			<FontAwesomeIcon fixedWidth icon={icon} />
		</L1PlainButtonRoot>
	)
}

export const IconLabelButtonRoot = styled(L1PlainButtonRoot)(({theme}) => ({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'center',
	alignItems: 'center',
	columnGap: theme.spacing(1),
}))

interface L1IconLabelButtonProps {
	label: string
	icon: IconProp
	color?: FusionColor
	onClick?: React.MouseEventHandler<HTMLButtonElement>
}

export const L1IconLabelButton = ({label, icon, color, onClick}: L1IconLabelButtonProps) => {
	return (
		<IconLabelButtonRoot color={color} onClick={onClick}>
			<FontAwesomeIcon icon={icon} />
			<div>{label}</div>
		</IconLabelButtonRoot>
	)
}

export const L1LabelIconButton = ({label, icon, color, onClick}: L1IconLabelButtonProps) => {
	return (
		<IconLabelButtonRoot color={color} onClick={onClick}>
			<div>{label}</div>
			<FontAwesomeIcon icon={icon} />
		</IconLabelButtonRoot>
	)
}

type L1ButtonColor = FusionColor.White|FusionColor.OrangeLight1|FusionColor.OrangeLight2|FusionColor.BlueMain|
	FusionColor.Second|FusionColor.Third|FusionColor.StopRed|FusionColor.OrangeMain|FusionColor.Transparent|
	FusionColor.Text

const getButtonColoring = (theme: Theme, color?: L1ButtonColor, disabled: Boolean = false) => {
	if (disabled) {
		const hasBorder = color === FusionColor.White || color === FusionColor.Transparent
		return {
			backgroundColor: theme.colors.third,
			color: theme.colors.text,
			opacity: 0.4,
			border: hasBorder ? `1px solid ${theme.colors.miniHeader}` : 'none',
			'&:hover': {
				cursor: 'default',
			}
		}
	}

	switch(color) {
		case FusionColor.White:
			return {
				backgroundColor: theme.colors.white,
				color: theme.colors.blueMain,
				border: `1px solid ${theme.colors.lines}`,
			}
		case FusionColor.OrangeLight1:
			return {
				backgroundColor: theme.colors.orangeLight1,
				color: theme.colors.orangeDark,
			}
		case FusionColor.OrangeLight2:
			return {
				backgroundColor: theme.colors.orangeLight2,
				color: theme.colors.bloodOrange
			}
		case FusionColor.Second:
			return {
				backgroundColor: theme.colors.second,
				color: theme.colors.text,
			}
		case FusionColor.Third:
			return {
				backgroundColor: theme.colors.third,
				color: theme.colors.blueMain,
			}
		case FusionColor.StopRed:
			return {
				backgroundColor: theme.colors.stopRed,
				color: theme.colors.white,
			}
		case FusionColor.Transparent:
			return {
				backgroundColor: theme.colors.transparent,
				color: theme.colors.white,
				border: `1px solid ${theme.colors.white}`,
			}
		case FusionColor.BlueMain:
			return {
				backgroundColor: theme.colors.blueMain,
				color: theme.colors.white,
			}
		case FusionColor.Text:
			return {
				backgroundColor: theme.colors.third,
				color: theme.colors.text,
			}
		case FusionColor.OrangeMain:
		default:
			return {
				backgroundColor: theme.colors.orangeMain,
				color: theme.colors.white,
			}
	}
}

// button icon sizes...
// 20240620 14px seems a big closer to the latest designs
const smallIconSizeRem = `${14/16}rem`
const smallIconSizeEm = `${14/16}em`
const bigIconSizeRem = `${24/16}rem`

type ReasonableButtonRootProps = {
	color?: L1ButtonColor,
	wide?: boolean,
	short?: boolean,
	disabled?: boolean,
} & { theme: Theme }

const ReasonableButtonRoot = styled(IconLabelButtonRoot)(({theme, color, short, wide, disabled}: ReasonableButtonRootProps) => ({
	// 20250424 variations between the renderings of Source Sans Pro in Figma
	// vs the browser (even in Safari) have me leaning toward use of the
	// normal weight for buttons instead of the 600 specified --cblack
	// fontWeight: 600,
	padding: theme.spacing(short ? 0.75 : 1.5, wide ? 5 : 2),
	borderRadius: theme.borderRadius,
	...getButtonColoring(theme, color, disabled),
	'svg': {
		fontSize: short ? smallIconSizeEm : '1em',
	},
}))

interface L1ReasonableButtonProps {
	className?: string
	label: string
	icon?: IconProp
	rightIcon?: IconProp
	color?: L1ButtonColor
	wide?: boolean
	short?: boolean
	onClick?: React.MouseEventHandler<HTMLButtonElement>
	disabled?: boolean
	expand?: boolean,
	loading?: boolean
	// loading?: boolean
}

export const L1ReasonableButton = ({className, label, icon, rightIcon, color, short, wide, onClick, disabled = false, expand = false, loading = false}: L1ReasonableButtonProps) => {
	// const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
	// 	if(onClick && !loading) {
	// 		onClick(e)
	// 	}
	// }, [onClick, loading])
	// let iconNode: ReactNode = null
	// if(loading) {
	// 	iconNode = <Loading size="1x" />
	// } else if(icon) {
	// 	iconNode = <FontAwesomeIcon icon={icon} />
	// }
	return (
		<ReasonableButtonRoot className={className} color={color} short={short} wide={wide} onClick={!disabled ? onClick : undefined} disabled={disabled}>
			{icon ? loading ? <FontAwesomeIcon className='fa-spin' icon={faSpinner} /> : <FontAwesomeIcon icon={icon} /> : null}
			{label ? <div style={expand ? {width: 'max-content'} : {}}>{label}</div> : null}
			{rightIcon ? loading ? <FontAwesomeIcon className='fa-spin' icon={faSpinner} /> : <FontAwesomeIcon icon={rightIcon} /> : null}
		</ReasonableButtonRoot>
	)
}

interface SmallIconButtonRootProps {
	color?: L1ButtonColor,
	disabled: boolean
}

const SmallIconButtonRoot = styled(IconLabelButtonRoot)<SmallIconButtonRootProps>(({theme, color, disabled}) => ({
	position: 'relative',
	fontSize: smallIconSizeRem,
	padding: theme.spacing(1.25),
	borderRadius: theme.borderRadius,
	...getButtonColoring(theme, color, disabled),
	userSelect: 'none',
}))

interface L1SmallIconButtonProps {
	icon: IconProp,
	color?: L1ButtonColor,
	notification?: boolean,
	onClick?: React.MouseEventHandler<HTMLButtonElement>|undefined,
	disabled?: boolean
}

export const L1SmallIconButton = ({icon, color, notification, onClick, disabled = false}: L1SmallIconButtonProps) => {
	return (
		<SmallIconButtonRoot color={color} onClick={!disabled ? onClick : undefined} disabled={disabled}>
			<FontAwesomeIcon fixedWidth icon={icon} />
			{notification ? <L1NotificationDotPositioner><L1NotificationDot /></L1NotificationDotPositioner> : null}
		</SmallIconButtonRoot>
	)
}

type BigIconButtonRootProps = {
	color?: L1ButtonColor,
	disabled: boolean,
} & { theme: Theme }

const BigIconButtonRoot = styled(IconLabelButtonRoot)(({theme, color, disabled}: BigIconButtonRootProps) => ({
	bigIconSizeRem,
	padding: theme.spacing(2),
	borderRadius: theme.borderRadius,
	...getButtonColoring(theme, color, disabled),
}))

interface L1BigIconButtonProps {
	icon: IconProp,
	color?: L1ButtonColor,
	onClick?: React.MouseEventHandler<HTMLButtonElement>|undefined,
	disabled?: boolean
}

export const L1BigIconButton = ({icon, color, onClick, disabled = false}: L1BigIconButtonProps) => {
	return (
		<BigIconButtonRoot color={color} onClick={!disabled ? onClick : undefined} disabled={disabled}>
			<FontAwesomeIcon fixedWidth icon={icon} />
		</BigIconButtonRoot>
	)
}
